import { useEffect, useRef, useState } from 'react'
import Modal from "../Modal";
import ModalClose from "../Modal/ModalClose";
import PropertyDetailsForm from "../PropertyDetailsForm";
import EditTab from "../EditTab";
import AddButton from "../IconButton";
import DefaultButton from "../Button/Button";
import FormInput from "../Input";
import addIcon from "../../assets/icons/Addimg.svg";
import ResidentService from "../../api/resident-service";
import { useParams } from "react-router-dom"; 
import EstateService from '../../api/estate-service'
import { EstateDto } from '../../interfaces/estate'
import Spinner from '../Spinner'
import ImageCarousel from '../Estate/ImageCarousel'
import ResidentType from '../../components/Resident/ResidentType';
import BankAccountVerification from '../Account/BankAccountVerification';

interface ViewResidentTypesProps {
  setNumOfResidentTypes: (num: number) => void
}

const ButtonTab = ({ setNumOfResidentTypes }: ViewResidentTypesProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { estateId } = useParams<{ estateId: string }>(); 
  const [openTab, setOpenTab] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [estateData, setEstateData] = useState<EstateDto | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (estateId) {
      EstateService.getEstateById(estateId)
        .then((response) => {
          setEstateData(response.data as EstateDto)
        })
        .catch((error) => console.error('Failed to fetch estate data:', error))
        .finally(() => setIsLoaded(true))
    }
  }, [estateId])

  useEffect(() => {
    if (estateData && estateData.urls) {
      setImageUrls(estateData.urls)
    }
  }, [estateData])

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleViewSelect = (view: number) => {
    if (openTab !== view) {
      setOpenTab(view)
      updateURLParams(view)
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    try {
      await new Promise((res) => setTimeout(res, 1500))
      // Show success toast or clear form
      // console.log('Form submitted')
      setIsModalOpen(false)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }
  

  const { data } = ResidentService.useGetResidentTypeList(
    estateId || "",
    {
      page: currentPage,
    }
  );

  useEffect(() => {
    if (data?.totalResults) {
      setNumOfResidentTypes(data.totalResults);
    }
  }, [data?.totalResults, setNumOfResidentTypes]);

  const updateURLParams = (activeTab: number) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("active", activeTab.toString());
    const newURL = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.replaceState(null, "", newURL);
  };

  const getActiveTabFromURL = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const activeTab = queryParams.get("active");
    return activeTab ? parseInt(activeTab) : 1;
  };

  useEffect(() => {
    setOpenTab(getActiveTabFromURL());
    setIsLoaded(true);
  }, []);

  if (!isLoaded) {
    return <p>Loading...</p>;
  }

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0]
      setSelectedFile(selectedFile)
      setIsUploading(true)

      try {
        const uploadedImageUrl = await EstateService.uploadPhoto(selectedFile)
        setImageUrls((prevUrls) => [...prevUrls, uploadedImageUrl])
      } catch (error) {
        console.error('Error uploading image:', error)
      } finally {
        setIsUploading(false)
      }
    }
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }
  const handleDelete = (index: number) => {
    setImageUrls((prevUrls) => prevUrls.filter((_, i) => i !== index))
  }

  return (
    <>
      <div className="flex flex-col">
        <ul
          className="flex mb-0 list-none pt-3 pb-2 flex-row gap-1 md:gap-3"
          role="tablist"
        >
          <EditTab
            label="Property details"
            isActive={openTab === 1}
            onClick={() => handleViewSelect(1)}
          />
          <EditTab
            label="Resident type"
            isActive={openTab === 2}
            onClick={() => handleViewSelect(2)}
          />
          <EditTab
            label="Subscriptions"
            isActive={openTab === 3}
            onClick={() => handleViewSelect(3)}
          />
          <EditTab
            label="Account"
            isActive={openTab === 4}
            onClick={() => handleViewSelect(4)}
          />
        </ul>
        <div className="relative flex flex-col min-w-0 break-words bg-white w-full rounded">
          <div className="py-5 flex-auto">
            <div className="tab-content tab-space">
              <div className={openTab === 1 ? 'block' : 'hidden'} id="PropertyDetails">
                <div>
                  <div className="flex flex-col gap-6 mb-6">
                    <div className="flex gap-7 items-center">
                      {isUploading ? (
                      <div className="relative w-[120px] h-[68px] sm:w-[150px] sm:h-[85px] md:w-[300px] md:h-[199px] flex items-center justify-center bg-gray-100">
                        <Spinner loading />
                      </div>
                      ) : (
                        <ImageCarousel
                          imageUrls={imageUrls}
                          handleDelete={handleDelete}
                        />
                      )}
                    </div>
                    <div>
                      <AddButton
                        buttonStyle="w-full sm:w-[195px]"
                        icon={<img src={addIcon} alt="Add icon" />}
                        handleModalOpen={triggerFileInput}
                      >
                        <p className="text-base font-medium">Add image</p>
                      </AddButton>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-6">
                    <p className="font-bold text-lg">Property details</p>
                    <PropertyDetailsForm 
                     imageUrls={imageUrls}
                     estateData={estateData} 
                      onUpdate={(updatedEstate) => setEstateData(updatedEstate)}/>
                  </div>
                </div>
              </div>
              <div className={openTab === 2 ? "block" : "hidden"} id="link2">
                <ResidentType
                  estateId={estateId || ""} 
                  currentPage={currentPage}
                  setNumOfResidentType={setNumOfResidentTypes}
                />
              </div>
              <div className={openTab === 3 ? 'block' : 'hidden'} id="link3">
                <form action="" className="w-full sm:max-w-md">
                  <div className="space-y-2.5">
                    <p className="">SubcriptionType</p>
                    <p className="border border-[#E9ECEF] rounded-lg p-2.5 text-[#6C757D]">
                      paid
                    </p>
                  </div>
                </form>
              </div>
                <div className={openTab === 4 ? "block" : "hidden"} id="link2">
                <BankAccountVerification estateId={estateId || ""} />
              </div>
            </div>
          </div>
        </div>
        <Modal
          open={isModalOpen}
          close={handleCloseModal}
          widthClass="w-[calc(100%-32px)] sm:max-w-lg md:max-w-[776px]"
          modalClassName="p-4 md:p-8 max-h-[90vh] overflow-y-auto flex flex-col"
        >
          <ModalClose onClick={handleCloseModal} />
          <div className="py-10 px-12">
            <form onSubmit={handleSubmit}>
              <div className="flex gap-8 mb-5">
                <FormInput
                  label="Resident type"
                  placeholder="e.g Bungalow"
                  size="lg"
                  name="residentType"
                />
                <FormInput
                  label="Levy fee in Naira"
                  placeholder="e.g 1000"
                  size="lg"
                  name="levy"
                />
              </div>
              <div className="flex flex-col gap-2 mb-5">
                <label htmlFor="description" className="text-lg font-medium">
                  Description
                </label>
                <textarea
                  name=""
                  id="description"
                  rows={4}
                  className="border border-solid rounded-lg"
                ></textarea>
              </div>
              <DefaultButton
               background="gray.900"
               color="white"
               type="submit"
               size="lg"
               loading={loading}
               text="Add" 
              // className="float-right mt-5"
              />
            </form>
          </div>
        </Modal>
      </div>
    </>
  )
}

export default ButtonTab
